<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useLaboratoryStore } from '../stores';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import type { Laboratory } from '../types';
import OrderModal from './laboratory/OrderModal.vue';

const laboratoryStore = useLaboratoryStore();
const selectedLab = ref<Laboratory | null>(null);
const showOrderModal = ref(false);

onMounted(async () => {
  await laboratoryStore.fetchLaboratories();
});

const openWhatsApp = (whatsapp: string) => {
  window.open(`https://wa.me/${whatsapp}`, '_blank');
};

const handleOrder = (lab: Laboratory) => {
  if (lab.WhatsApp && !lab.OrderUrl) {
    selectedLab.value = lab;
    showOrderModal.value = true;
  } else if (lab.OrderUrl) {
    window.open(lab.OrderUrl, '_blank');
  }
};
</script>

<template>
  <div>
    <h2 class="text-2xl font-bold text-slate-800 mb-6">Laboratorios Disponibles</h2>

    <div v-if="laboratoryStore.loading" class="text-center py-8">
      <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500 mx-auto"></div>
    </div>

    <div v-else-if="laboratoryStore.error" class="text-center py-8 text-red-600">
      {{ laboratoryStore.error }}
    </div>

    <div v-else-if="laboratoryStore.laboratories.length === 0" class="text-center py-8 text-slate-600">
      No hay laboratorios disponibles.
    </div>

    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <div
        v-for="lab in laboratoryStore.laboratories"
        :key="lab.id"
        class="bg-white rounded-lg shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow duration-200"
      >
        <h3 class="text-lg font-semibold text-slate-800 mb-4">{{ lab.Nombre }}</h3>
        <div class="space-y-2 text-sm text-slate-600">
          <p>
            <span class="inline-block w-20 font-medium">Ubicación:</span>
            {{ lab.Ubicación }}
          </p>
          <p>
            <span class="inline-block w-20 font-medium">Contacto:</span>
            {{ lab.Contacto }}
          </p>
          <p>
            <span class="inline-block w-20 font-medium">Teléfono:</span>
            {{ lab.Teléfono }}
          </p>
        </div>
        
        <div class="mt-4 flex gap-2">
          <button
            v-if="lab.WhatsApp"
            @click="openWhatsApp(lab.WhatsApp)"
            class="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
          >
            <font-awesome-icon :icon="['fab', 'whatsapp']" />
            <span>WhatsApp</span>
          </button>
          
          <button
            v-if="lab.OrderUrl || lab.WhatsApp"
            @click="handleOrder(lab)"
            class="flex items-center gap-2 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors"
          >
            <font-awesome-icon :icon="['fas', 'link']" />
            <span>Hacer Pedido</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de Pedido por WhatsApp -->
    <OrderModal
      v-if="selectedLab"
      :show="showOrderModal"
      :laboratory="selectedLab"
      @close="showOrderModal = false"
    />
  </div>
</template>